/**
 * Obligation derivation — build doc §4.2 Flow C.
 *
 * Provisions that carry recurring or dated duties (ESG reporting, custom
 * reporting frequency, transparency packages, annual tax letters) become rows
 * on the obligation calendar. Pure function: the caller supplies the
 * reference date so behavior is reproducible in tests and evals.
 */

import type { ExtractedProvision, ObligationDraft } from "./types";

const OBLIGATION_CATEGORIES = new Set(["esg", "reporting_freq", "transparency", "tax_rep"]);

function quarterEnd(d: Date): Date {
  const q = Math.floor(d.getUTCMonth() / 3);
  // Last day of the quarter: first day of next quarter minus 1.
  return new Date(Date.UTC(d.getUTCFullYear(), q * 3 + 3, 0));
}

function halfEnd(d: Date): Date {
  const m = d.getUTCMonth();
  return m < 6
    ? new Date(Date.UTC(d.getUTCFullYear(), 6, 0)) // Jun 30
    : new Date(Date.UTC(d.getUTCFullYear(), 12, 0)); // Dec 31
}

function yearEnd(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), 12, 0));
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function nextDueDate(
  frequency: NonNullable<ObligationDraft["frequency"]>,
  reference: Date,
): string | null {
  switch (frequency) {
    case "quarterly": {
      const qe = quarterEnd(reference);
      return iso(
        qe.getTime() <= reference.getTime() ? quarterEnd(new Date(qe.getTime() + 86400000)) : qe,
      );
    }
    case "semi_annual": {
      const he = halfEnd(reference);
      return iso(
        he.getTime() <= reference.getTime() ? halfEnd(new Date(he.getTime() + 86400000)) : he,
      );
    }
    case "annual": {
      const ye = yearEnd(reference);
      return iso(
        ye.getTime() <= reference.getTime() ? yearEnd(new Date(ye.getTime() + 86400000)) : ye,
      );
    }
    case "one_time":
    case "on_event":
      return null;
  }
}

const TITLES: Record<string, string> = {
  esg: "ESG report",
  reporting_freq: "Custom report package",
  transparency: "Information package",
  tax_rep: "Tax information delivery",
};

export function deriveObligations(
  provisions: ExtractedProvision[],
  referenceDate: Date,
): ObligationDraft[] {
  const drafts: ObligationDraft[] = [];
  provisions.forEach((p, index) => {
    if (!OBLIGATION_CATEGORIES.has(p.category)) return;
    const frequency = p.structuredValue?.frequency ?? null;
    if (!frequency) return; // no detectable cadence — nothing to calendar

    drafts.push({
      provisionIndex: index,
      title: `${TITLES[p.category] ?? "Obligation"} (${frequency.replaceAll("_", "-")})`,
      description: p.summary,
      obligationType: p.category === "tax_rep" ? "notice" : "reporting",
      frequency,
      nextDueDate: nextDueDate(frequency, referenceDate),
    });
  });
  return drafts;
}
